import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import {
  TextField,
  Button,
  Container,
  Typography,
  Box,
  Grid,
  Avatar,
  InputAdornment,
  Fade,
  Chip,
  Paper,
  Skeleton,
} from "@mui/material";
import {
  Calendar,
  Clock,
  Search,
  User,
  BookOpen,
  TrendingUp,
  Sparkles,
  ArrowRight,
  Menu,
  ChevronDown,
  ArrowUp,
} from "lucide-react";

import BlogDetail from "./BlogDetail.jsx";
import { useDispatch, useSelector } from "react-redux";
import {
  getBlogsCategories,
  getBlogsList,
} from "../../features/user/userSlice.js";
import Loader from "../../components/common/Loader.jsx";

import Seo from "../../components/Seo";
import Breadcrumb from "../../components/Breadcrumb";
import { generateBreadcrumbs } from "../../utils/breadcrumbs";

export default function Blog() {
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [visibleCount, setVisibleCount] = useState(12); // 👈 initially show 3 blogs
  const [showBackToTop, setShowBackToTop] = useState(false);
  const { blogs, blogsCategories, blogLoading, blogError } = useSelector(
    (state) => state.user
  );
  // console.log("Blogs:", blogs);
  // console.log("Blogs cat:", blogsCategories);
  useEffect(() => {
    dispatch(getBlogsCategories());
    dispatch(getBlogsList());
  }, [dispatch]);

  const filteredPosts = blogs.filter((post) => {
    const matchesSearch =
      post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory =
      !selectedCategory || post.category_id === selectedCategory;
    return matchesSearch && matchesCategory;
    // && post.status === "published";
  });

  // console.log("Sample Blog:", blogs[0]);
  // console.log("FIltered Posts:", filteredPosts);

  const getCategoryName = (categoryId) =>
    blogsCategories.find((cat) => cat.id === categoryId)?.name ||
    "Uncategorized";

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  const getReadingTime = (content) => {
    const words = content.replace(/<[^>]*>/g, "").split(" ").length;
    const minutes = Math.ceil(words / 200);
    return `${minutes} min read`;
  };

  //  Show button when scrolled down
  useEffect(() => {
    const handleScroll = () => {
      // show button only if more than 6 blogs are visible on screen
      if (visibleCount > 12) {
        setShowBackToTop(window.scrollY > 800);
      } else {
        setShowBackToTop(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [visibleCount]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setVisibleCount(12);
  };

  const breadcrumbs = generateBreadcrumbs({ pageType: "blog" });

  if (blogError) {
    return (
      <Box
        sx={{
          minHeight: "50vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Typography color="error">{blogError}</Typography>
      </Box>
    );
  }

  // if (blogLoading) {
  //   return (
  //     <div
  //       style={{
  //         padding: theme.spacing(5),
  //         minHeight: "100vh",
  //         display: "flex",
  //         alignItems: "center",
  //         justifyContent: "center",
  //       }}
  //     >
  //       <Loader />
  //     </div>
  //   );
  // }

  return (
    <>
      <Seo
        title="ScrapTrade Blog — Insights, Trends & Scrap Market News"
        description="Stay updated with ScrapTrade Blog. Get the latest scrap market trends, recycling tips, trading insights, and industry news."
        url="https://scraptrade.com.au/blog"
        canonical="https://scraptrade.com.au/blog"
        image="https://scraptrade.com.au/assets/og/blog.jpg"
        breadcrumbs={breadcrumbs}
        extraSchema={{
          "@context": "https://schema.org",
          "@type": "Blog",
          headline: "ScrapTrade Blog",
          description:
            "Latest updates on scrap trading, recycling, and global market insights.",
          image: "https://scraptrade.com.au/assets/og/blog.jpg",
          author: {
            "@type": "Organization",
            name: "ScrapTrade",
          },
          publisher: {
            "@type": "Organization",
            name: "ScrapTrade",
            logo: {
              "@type": "ImageObject",
              url: "https://scraptrade.com.au/assets/logo.png",
            },
          },
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": "https://scraptrade.com.au/blog",
          },
        }}
      />

      <div style={{ minHeight: "100vh", backgroundColor: "#f8fafc" }}>
        {/* Professional Business Header */}
        <Box
          sx={{
            background:
              "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)",
            position: "relative",
            overflow: "hidden",
            //  CHANGE: responsive vertical padding for hero
            py: { xs: 8, md: 20 },
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23F4B942' fill-opacity='0.05'%3E%3Ccircle cx='9' cy='9' r='1'/%3E%3Ccircle cx='51' cy='51' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              zIndex: 1,
            },
          }}
        >
          {/* Floating Accent Elements */}
          <Box
            sx={{
              position: "absolute",
              top: "20%",
              right: "10%",
              width: "100px",
              height: "100px",
              background: "linear-gradient(135deg, #F4B942 0%, #E6A83A 100%)",
              borderRadius: "50%",
              opacity: 0.1,
              animation: "float 8s ease-in-out infinite",
              zIndex: 1,
              "@keyframes float": {
                "0%, 100%": { transform: "translateY(0px)" },
                "50%": { transform: "translateY(-30px)" },
              },
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: "30%",
              left: "5%",
              width: "60px",
              height: "60px",
              background: "linear-gradient(135deg, #F4B942 0%, #E6A83A 100%)",
              borderRadius: "12px",
              opacity: 0.08,
              animation: "float 10s ease-in-out infinite reverse",
              zIndex: 1,
            }}
          />

          <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
            <Fade in={true} timeout={1200}>
              <Box textAlign="center">
                {/* Premium Badge */}
                <Box sx={{ mb: 5 }}>
                  <Chip
                    icon={<Sparkles size={18} />}
                    label="Premium Business Insights 2025"
                    sx={{
                      bgcolor: "rgba(244, 185, 66, 0.15)",
                      color: "#F4B942",
                      border: "1px solid rgba(244, 185, 66, 0.4)",
                      fontWeight: 700,
                      fontSize: "0.95rem",
                      py: 2.5,
                      px: 2,
                      backdropFilter: "blur(10px)",
                      boxShadow: "0 8px 32px rgba(244, 185, 66, 0.2)",
                      "& .MuiChip-icon": {
                        color: "#F4B942",
                      },
                    }}
                  />
                </Box>

                {/* Main Heading with Gradient Text */}
                <Typography
                  variant="h1"
                  sx={{
                    //  CHANGE: responsive font sizes for heading
                    fontSize: {
                      xs: "2.2rem",
                      sm: "3rem",
                      md: "5rem",
                      lg: "6rem",
                    },
                    fontWeight: 900,
                    lineHeight: 1.1,
                    mb: 5,
                    background:
                      "linear-gradient(135deg, #ffffff 0%, #F4B942 50%, #ffffff 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    letterSpacing: "-0.05em",
                    textShadow: "0 0 40px rgba(244, 185, 66, 0.3)",
                  }}
                >
                  Business
                  <Box component="span" sx={{ display: "block", mt: 1 }}>
                    Insights
                  </Box>
                </Typography>

                {/* Enhanced Subtitle */}
                <Typography
                  variant="h4"
                  sx={{
                    //  CHANGE: responsive subtitle font sizes
                    fontSize: { xs: "1rem", sm: "1.3rem", md: "1.8rem" },
                    mb: 6,
                    maxWidth: "760px",
                    mx: "auto",
                    color: "rgba(255,255,255,0.85)",
                    fontWeight: 300,
                    lineHeight: 1.4,
                    letterSpacing: "-0.01em",
                  }}
                >
                  Expert insights and strategic analysis for
                  <Box
                    component="span"
                    sx={{ color: "#F4B942", fontWeight: 500 }}
                  >
                    {" "}
                    modern business leaders{" "}
                  </Box>
                  navigating tomorrow's challenges
                </Typography>

                {/* Premium CTA Group */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 4,
                    justifyContent: "center",
                    flexWrap: "wrap",
                    mb: 8,
                  }}
                >
                  <Button
                    variant="contained"
                    size="large"
                    endIcon={<TrendingUp size={22} />}
                    sx={{
                      bgcolor: "#F4B942",
                      color: "#000",
                      fontWeight: 800,
                      px: 4,
                      py: 1.4,
                      borderRadius: "16px",
                      fontSize: "1.0rem",
                      textTransform: "none",
                      boxShadow: "0 12px 40px rgba(244, 185, 66, 0.4)",
                      "&:hover": {
                        bgcolor: "#E6A83A",
                        transform: "translateY(-3px)",
                        boxShadow: "0 16px 50px rgba(244, 185, 66, 0.6)",
                      },
                      transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    Explore Articles
                  </Button>

                  <Button
                    variant="outlined"
                    size="large"
                    endIcon={<ArrowRight size={22} />}
                    sx={{
                      color: "white",
                      borderColor: "rgba(255,255,255,0.4)",
                      fontWeight: 700,
                      px: 4,
                      py: 1.4,
                      borderRadius: "16px",
                      fontSize: "1.0rem",
                      textTransform: "none",
                      borderWidth: "2px",
                      backdropFilter: "blur(10px)",
                      "&:hover": {
                        borderColor: "#F4B942",
                        bgcolor: "rgba(244, 185, 66, 0.1)",
                        transform: "translateY(-3px)",
                        boxShadow: "0 12px 40px rgba(244, 185, 66, 0.2)",
                      },
                      transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    Learn More
                  </Button>
                </Box>
              </Box>
            </Fade>
          </Container>
        </Box>

        {/* Additional Search Bar Before Categories */}
        {/*  CHANGE: converted outer wrapper to MUI Box and added responsive horizontal padding */}
        <Box
          sx={{
            maxWidth: "1200px",
            mx: "auto",
            mt: 6,
            mb: 4,
            px: { xs: 2, md: 4 },
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
            <Box sx={{ maxWidth: "600px", width: "100%" }}>
              <Paper
                elevation={0}
                sx={{
                  p: 1,
                  px: 2,
                  py: 1,
                  borderRadius: "16px",
                  background: "rgba(255, 255, 255, 0.9)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(244, 185, 66, 0.2)",
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                }}
              >
                <TextField
                  fullWidth
                  placeholder="Filter articles by keyword..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search size={20} color="#F4B942" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "transparent",
                      borderRadius: "14px",
                      height: "50px",
                      fontSize: "1rem",
                      fontWeight: 500,
                      "& fieldset": {
                        border: "none",
                      },
                      "&:hover fieldset": {
                        border: "none",
                      },
                      "&.Mui-focused fieldset": {
                        border: "none",
                      },
                      "& input::placeholder": {
                        color: "#6b7280",
                        fontWeight: 500,
                      },
                    },
                  }}
                />
              </Paper>
            </Box>
          </Box>
        </Box>

        {/* Category Filter Section */}
        {/*  CHANGE: use Box with responsive px so chips/buttons don't overflow on small screens */}
        <Box
          sx={{
            maxWidth: "1200px",
            mx: "auto",
            mb: 6,
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            justifyContent: "center",
            px: { xs: 2, md: 0 },
          }}
        >
          <Box
            component="button"
            onClick={() => setSelectedCategory(null)}
            sx={{
              px: 2,
              py: 0.7,
              borderRadius: "999px",
              backgroundColor:
                selectedCategory === null ? "#f59e0b" : "#e2e8f0",
              color: selectedCategory === null ? "#fff" : "#1e293b",
              fontWeight: 600,
              border: "none",
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              "&:focus": { outline: "none" },
            }}
          >
            All
          </Box>

          {blogsCategories.map((cat) => (
            <Box
              component="button"
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              sx={{
                px: 2,
                py: 0.7,
                borderRadius: "999px",
                backgroundColor:
                  selectedCategory === cat.id ? "#f59e0b" : "#e2e8f0",
                color: selectedCategory === cat.id ? "#fff" : "#1e293b",
                fontWeight: 600,
                border: "none",
                cursor: "pointer",
                transition: "all 0.2s ease-in-out",
                "&:focus": { outline: "none" },
              }}
            >
              {cat.name}
            </Box>
          ))}
        </Box>

        {/* Blog Cards Section */}
        <Box sx={{ backgroundColor: "#f8fafc", py: { xs: 4, md: 6 } }}>
          <Container maxWidth="lg">
            {/* Section Heading */}
            <Box sx={{ textAlign: "center", mb: 6 }}>
              <Typography
                sx={{
                  fontSize: { xs: "1.8rem", md: "2.5rem" },
                  fontWeight: 700,
                  color: "#1e293b",
                  mb: 1,
                }}
              >
                Featured Articles
              </Typography>
            </Box>

            {/* Grid Section */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))",
                gap: "2rem",
                "@media (max-width:1100px)": {
                  gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
                },
                "@media (max-width:800px)": {
                  gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                },
                "@media (max-width:480px)": {
                  gridTemplateColumns: "repeat(1, 1fr)",
                },
              }}
            >
              {/* --- Skeletons (loading or no blogs yet) --- */}
              {blogLoading || blogs.length === 0 ? (
                Array.from(new Array(6)).map((_, i) => (
                  <Box
                    key={i}
                    sx={{
                      backgroundColor: "white",
                      borderRadius: "12px",
                      overflow: "hidden",
                      p: 2,
                      boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                    }}
                  >
                    <Skeleton variant="rectangular" height={200} />
                    <Box sx={{ mt: 2 }}>
                      <Skeleton width="60%" height={30} />
                      <Skeleton width="90%" height={20} />
                      <Skeleton width="40%" height={20} />
                      <Box
                        sx={{ display: "flex", alignItems: "center", mt: 2 }}
                      >
                        <Skeleton variant="circular" width={32} height={32} />
                        <Skeleton width="40%" height={20} sx={{ ml: 2 }} />
                      </Box>
                    </Box>
                  </Box>
                ))
              ) : filteredPosts.length > 0 ? (
                /* --- Blog Cards --- */
                filteredPosts.slice(0, visibleCount).map((post) => (
                  <Link
                    to={`/blog/${post.slug}`}
                    key={post.id}
                    style={{ textDecoration: "none" }}
                  >
                    <Box
                      component="article"
                      sx={{
                        backgroundColor: "white",
                        borderRadius: "12px",
                        overflow: "hidden",
                        boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
                        transition: "all 0.3s ease",
                        border: "1px solid #e2e8f0",
                        cursor: "pointer",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
                          borderColor: "#f59e0b",
                        },
                      }}
                    >
                      {/* Thumbnail */}
                      <Box sx={{ position: "relative", height: 200 }}>
                        <Box
                          component="img"
                          src={post.feature_image_url}
                          alt={post.title}
                          loading="lazy"
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                        <Box
                          sx={{
                            position: "absolute",
                            top: "1rem",
                            left: "1rem",
                            bgcolor: "#1e293b",
                            color: "#f59e0b",
                            px: 2,
                            py: 0.5,
                            borderRadius: "6px",
                            fontSize: "0.75rem",
                            fontWeight: 600,
                          }}
                        >
                          {getCategoryName(post.category_id)}
                        </Box>
                      </Box>

                      {/* Content */}
                      <Box sx={{ p: 2 }}>
                        <Typography
                          component="h3"
                          sx={{
                            fontSize: "1.25rem",
                            fontWeight: 700,
                            color: "#1e293b",
                            mb: 1,
                            overflow: "hidden",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {post.title}
                        </Typography>

                        <Typography
                          sx={{
                            color: "#64748b",
                            fontSize: "0.875rem",
                            mb: 2,
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {post.excerpt}
                        </Typography>

                        {/* Footer */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            pt: 1,
                            borderTop: "1px solid #f1f5f9",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Box
                              component="img"
                              src={post.author.profile_image_url}
                              alt={post.author.name}
                              loading="lazy"
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: "50%",
                                objectFit: "cover",
                              }}
                            />
                            <Typography
                              sx={{ fontWeight: 600, fontSize: "0.875rem" }}
                            >
                              {post.author.name}
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <Clock size={14} />
                            <Typography
                              sx={{ fontSize: "0.875rem", color: "#64748b" }}
                            >
                              {getReadingTime(post.content)}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Link>
                ))
              ) : (
                /* --- Empty State --- */
                <Box sx={{ textAlign: "center", py: 6 }}>
                  <Typography
                    sx={{ fontSize: "1.5rem", color: "#64748b", mb: 1 }}
                  >
                    No articles found
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Load More button */}
            {!blogLoading && filteredPosts.length > visibleCount && (
              <Box sx={{ textAlign: "center", mt: 4 }}>
                <Button
                  variant="contained"
                  onClick={() => setVisibleCount((prev) => prev + 3)}
                  sx={{
                    bgcolor: "#f59e0b",
                    color: "white",
                    fontWeight: 600,
                    borderRadius: "10px",
                    px: 4,
                    py: 1.5,
                    "&:hover": { bgcolor: "#eab308" },
                  }}
                >
                  Load More
                </Button>
              </Box>
            )}
          </Container>
        </Box>

        <Fade in={showBackToTop}>
          <Button
            onClick={scrollToTop}
            variant="contained"
            sx={{
              position: "fixed",
              bottom: 20,
              right: 20,
              borderRadius: "50%",
              minWidth: "50px",
              width: "50px",
              height: "50px",
              bgcolor: "#f59e0b",
              color: "white",
              boxShadow: "0 6px 12px rgba(0,0,0,0.2)",
              "&:hover": { bgcolor: "#eab308" },
              zIndex: 9999,
            }}
          >
            <ArrowUp size={20} />
          </Button>
        </Fade>
      </div>
    </>
  );
}
